import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { getTrackerTagsApi } from "../../../DAL/trackerTag/trackerTag";
import ManageTagsDrawer from "./ManageTagsDrawer";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary)";

/**
 * Pick tags for a tracker from the shared library (no free-text create).
 * Value stays string[] on the tracker for backward compatibility.
 */
export default function TrackerLibraryTagPicker({
  value = [],
  onChange,
  description = "Select tags from the shared library. These are available on submissions for filters and automations — not auto-applied when someone joins.",
}) {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTrackerTagsApi({ limit: 500, status: "active" });
      setLibrary(Array.isArray(res?.tags) ? res.tags : []);
    } catch (_e) {
      setLibrary([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  // Keep this tracker's picked tags in step with renames / deletes made in
  // the Manage tags drawer.
  const handleLibraryChanged = ({ type, tag, prevName }) => {
    loadLibrary();
    const sameName = (a, b) =>
      String(a || "").toLowerCase() === String(b || "").toLowerCase();
    if (type === "deleted") {
      if (selected.some((name) => sameName(name, tag?.name))) {
        onChange?.(selected.filter((name) => !sameName(name, tag?.name)));
      }
      return;
    }
    if (prevName && tag?.name && !sameName(prevName, tag.name)) {
      if (selected.some((name) => sameName(name, prevName))) {
        onChange?.(
          selected.map((name) => (sameName(name, prevName) ? tag.name : name))
        );
      }
    }
  };

  const options = useMemo(() => {
    const names = (library || [])
      .map((t) => String(t?.name || "").trim())
      .filter(Boolean);
    // Keep any already-selected tags that may be inactive / legacy free-text.
    selected.forEach((name) => {
      if (
        name &&
        !names.some((n) => n.toLowerCase() === String(name).toLowerCase())
      ) {
        names.push(name);
      }
    });
    return names;
  }, [library, selected]);

  const colorByName = useMemo(() => {
    const map = {};
    (library || []).forEach((t) => {
      if (t?.name) map[String(t.name).toLowerCase()] = t.color || PRIMARY;
    });
    return map;
  }, [library]);

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.25 }}>
        {description}{" "}
        <Box
          component="button"
          type="button"
          onClick={() => setManageOpen(true)}
          sx={{
            p: 0,
            border: 0,
            background: "none",
            cursor: "pointer",
            font: "inherit",
            color: PRIMARY,
            fontWeight: 700,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Manage tags
        </Box>
      </Typography>

      <ManageTagsDrawer
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onChanged={handleLibraryChanged}
      />

      <Autocomplete
        multiple
        options={options}
        value={selected}
        loading={loading}
        disableCloseOnSelect
        onChange={(_e, next) => onChange?.(next)}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((option, index) => {
            const color = colorByName[String(option).toLowerCase()] || PRIMARY;
            const props = getTagProps({ index });
            return (
              <Chip
                {...props}
                key={option}
                label={option}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#fff",
                  border: `1px solid ${BORDER}`,
                  "& .MuiChip-avatar": { display: "none" },
                  "&::before": {
                    content: '""',
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: color,
                    mr: 0.75,
                    display: "inline-block",
                  },
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Select tags"
            placeholder={selected.length ? "" : "Search library tags…"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          loading
            ? "Loading…"
            : "No tags yet — add some with Manage tags"
        }
        sx={{ maxWidth: 560 }}
      />
    </Box>
  );
}
